import { contentApi } from '@/services/contentApi';

// ============================================================================
// Types
// ============================================================================

export type ReferenceType = 'agent' | 'prompt' | 'graph';

export interface Reference {
  type: ReferenceType;
  path: string;           // e.g., "agents/research.md" or just "research"
  range: {
    startLine: number;    // 1-indexed
    startColumn: number;  // 1-indexed
    endLine: number;
    endColumn: number;
  };
  fullMatch: string;      // The full matched text, e.g., "!agent agents/research.md"
}

export interface ResolvedReference {
  id: string;
  name: string;
  contentType: string;
  content?: string;
  folderId?: string;
}

// ============================================================================
// Reference Extraction
// ============================================================================

/**
 * Extract all !agent, !prompt, !graph references from content
 *
 * Matches patterns like:
 * - !agent agents/research.md
 * - !prompt prompts/system.md
 * - !graph graphs/workflow.md
 * - !agent research (without path)
 *
 * @param content - The content to parse
 * @returns Array of Reference objects with type, path, and position
 */
export function extractReferences(content: string): Reference[] {
  const references: Reference[] = [];
  const pattern = /!(agent|prompt|graph)\s+([^\s\n]+)/g;

  const lines = content.split('\n');

  lines.forEach((line, lineIndex) => {
    let match;
    // Reset regex lastIndex for each line
    pattern.lastIndex = 0;

    while ((match = pattern.exec(line)) !== null) {
      const type = match[1] as ReferenceType;
      const path = match[2];
      const fullMatch = match[0];

      references.push({
        type,
        path,
        range: {
          startLine: lineIndex + 1,       // Monaco uses 1-indexed lines
          startColumn: match.index + 1,   // Monaco uses 1-indexed columns
          endLine: lineIndex + 1,
          endColumn: match.index + fullMatch.length + 1,
        },
        fullMatch,
      });
    }
  });

  return references;
}

/**
 * Find a reference at a specific position in the content
 *
 * @param references - Array of references to search
 * @param line - Line number (1-indexed)
 * @param column - Column number (1-indexed)
 * @returns The reference at the position, or undefined
 */
export function findReferenceAtPosition(
  references: Reference[],
  line: number,
  column: number
): Reference | undefined {
  return references.find(
    (ref) =>
      line >= ref.range.startLine &&
      line <= ref.range.endLine &&
      column >= ref.range.startColumn &&
      column <= ref.range.endColumn
  );
}

// ============================================================================
// Reference Resolution
// ============================================================================

// Simple in-memory cache for resolved references
const resolvedCache = new Map<string, { data: ResolvedReference | null; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get cache key for a reference
 */
function getCacheKey(type: string, path: string): string {
  return `${type}:${path}`;
}

/**
 * Extract the name from a reference path
 * Handles both full paths ("agents/research.md") and simple names ("research")
 */
function extractNameFromPath(path: string): string {
  // Remove .md extension if present
  const withoutExt = path.replace(/\.md$/, '');

  // Get the last part of the path
  const parts = withoutExt.split('/');
  return parts[parts.length - 1];
}

/**
 * Resolve a reference to a content item
 *
 * @param type - The reference type (agent, prompt, graph)
 * @param path - The reference path
 * @param options - Optional parameters
 * @returns The resolved reference or null if not found
 */
export async function resolveReference(
  type: string,
  path: string,
  options: { includeContent?: boolean; skipCache?: boolean } = {}
): Promise<ResolvedReference | null> {
  const cacheKey = getCacheKey(type, path);

  // Check cache first
  if (!options.skipCache) {
    const cached = resolvedCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      // If we need content and it's not in cache, fetch it
      if (options.includeContent && cached.data && !cached.data.content) {
        try {
          const content = await contentApi.getContent(cached.data.id);
          cached.data.content = content;
        } catch {
          // Ignore content fetch errors for cache
        }
      }
      return cached.data;
    }
  }

  try {
    // Extract the name from the path
    const name = extractNameFromPath(path);

    // Map reference type to content type
    const contentType = type; // They're the same in this system

    // Search for items matching the name and type
    const items = await contentApi.listItems({
      contentType,
      limit: 10,
    });

    // Find the best match
    // Priority: exact name match > name in path match
    const exactMatch = items.find((item: any) => item.name === name);
    const pathMatch = items.find((item: any) =>
      path.includes(item.name) || item.name.includes(name)
    );

    const matchedItem = exactMatch || pathMatch;

    if (!matchedItem) {
      // Cache the negative result
      resolvedCache.set(cacheKey, { data: null, timestamp: Date.now() });
      return null;
    }

    const resolved: ResolvedReference = {
      id: matchedItem.id,
      name: matchedItem.name,
      contentType: matchedItem.content_type,
      folderId: matchedItem.folder_id,
    };

    // Fetch content if requested
    if (options.includeContent) {
      try {
        resolved.content = await contentApi.getContent(matchedItem.id);
      } catch (error) {
        console.warn(`Failed to fetch content for ${matchedItem.id}:`, error);
      }
    }

    // Cache the result
    resolvedCache.set(cacheKey, { data: resolved, timestamp: Date.now() });

    return resolved;
  } catch (error) {
    console.error(`Failed to resolve reference ${type}:${path}:`, error);
    return null;
  }
}

/**
 * Clear the resolution cache
 */
export function clearReferenceCache(): void {
  resolvedCache.clear();
}

/**
 * Remove a specific entry from the cache
 */
export function invalidateCacheEntry(type: string, path: string): void {
  const cacheKey = getCacheKey(type, path);
  resolvedCache.delete(cacheKey);
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Check if content contains any references
 */
export function hasReferences(content: string): boolean {
  const pattern = /!(agent|prompt|graph)\s+[^\s\n]+/;
  return pattern.test(content);
}

/**
 * Get all unique reference types in content
 */
export function getReferenceTypes(content: string): ReferenceType[] {
  const refs = extractReferences(content);
  const types = new Set<ReferenceType>(refs.map((r) => r.type));
  return Array.from(types);
}

/**
 * Batch resolve multiple references
 * More efficient than resolving one by one
 */
export async function resolveReferences(
  references: Reference[],
  options: { includeContent?: boolean } = {}
): Promise<Map<string, ResolvedReference | null>> {
  const results = new Map<string, ResolvedReference | null>();

  // Use Promise.all for parallel resolution
  await Promise.all(
    references.map(async (ref) => {
      const key = getCacheKey(ref.type, ref.path);
      const resolved = await resolveReference(ref.type, ref.path, options);
      results.set(key, resolved);
    })
  );

  return results;
}
